import { usePntSegmentsControllerGetLowerthirdByDate } from '@mi-front/api-v2';
import { toDateTimeStr, toTimestamp, useGraphCtx } from '@mi-front/core';
import { DurationForRange, StreamEl } from '@mi-front/ui';
import moment from 'moment-timezone';
import { transparentize } from 'polished';
import { useMemo } from 'react';
import { useActiveStation } from '../../../../../../../core/stations/hooks/useActiveStation';
import { useStreamSegmentsRange } from '../../../hooks/segments/useStreamSegmentsQuery';
import { useHoveredTs } from '../../focus/hovered-ts-ctx';
import { LowerthirdLabelWrapper } from './LowerthirdLabelWrapper';
import { normalizeLowerthirdByDateResponse } from './normalize-lowerthird-by-date-response';
import { DataItem, DisplayableLowerthird } from './types';
import { formatLowerthird } from './utils';

export function LowerthirdFocus() {
  const { height } = useGraphCtx();
  const hoveredTs = useHoveredTs();
  const range = useStreamSegmentsRange();
  const { slug } = useActiveStation();

  const { data, isLoading } = usePntSegmentsControllerGetLowerthirdByDate(slug, {
    from: toDateTimeStr(range.from),
    to: toDateTimeStr(range.to),
  });

  const lowerthirdItems = useMemo(() => normalizeLowerthirdByDateResponse(data), [data]);

  const formatted = useMemo(
    () => formatLowerthird(lowerthirdItems, isLoading),
    [lowerthirdItems, isLoading],
  );

  const hoveredLowethird = useMemo(
    () =>
      formatted?.find(
        (lowerthird: DataItem) =>
          hoveredTs &&
          toTimestamp(lowerthird.from) <= hoveredTs &&
          toTimestamp(lowerthird.to) > hoveredTs,
      ),
    [formatted, hoveredTs],
  );

  const lowerthirds = useMemo(
    () =>
      formatted?.map((lowerthird: DisplayableLowerthird, index: number) => {
        return (
          <StreamEl
            key={lowerthird.from}
            ts={hoveredTs ?? undefined}
            position="bottom"
            fullHeight
            zIndex={2}
            offsetX={5}
            checkOverflow
            style={{
              pointerEvents: 'none',
            }}
          >
            <LowerthirdLabelWrapper
              bgColor={lowerthird.color}
              style={{
                top: 4,
                left: 4,
                zIndex: 1,
                opacity: hoveredLowethird === lowerthird && hoveredTs ? 1 : 0,
              }}
            >
              <small className="font-weight-bold">
                <LowerthirdLabel index={index} lowerthird={lowerthird} />
              </small>
              {' - '}
              <small>
                {moment(lowerthird.from).format('HH:mm:ss')} (
                <DurationForRange from={lowerthird.from} to={lowerthird.to} />)
              </small>
            </LowerthirdLabelWrapper>
          </StreamEl>
        );
      }),
    [formatted, hoveredLowethird, hoveredTs],
  );

  if (!isLoading && !lowerthirdItems.length) {
    return null;
  }
  return (
    <div>
      {lowerthirds}
      {formatted?.map((lowerthird: DisplayableLowerthird) => (
        <StreamEl
          key={lowerthird.from}
          ts={toTimestamp(lowerthird.from)}
          position="bottom"
          fullHeight
          style={{
            pointerEvents: 'none',
          }}
        >
          {(scale) => {
            const width = scale(toTimestamp(lowerthird.to))! - scale(toTimestamp(lowerthird.from))!;
            return (
              <div
                style={{
                  position: 'absolute',
                  backgroundColor: transparentize(0.8, lowerthird.color),
                  height,
                  width,
                }}
              />
            );
          }}
        </StreamEl>
      ))}
    </div>
  );
}

export function LowerthirdLabel({
  lowerthird,
  index,
}: {
  lowerthird: DisplayableLowerthird;
  index: number;
}) {
  return <span>{lowerthird.text}</span>;
}
